import React, { useState, useMemo, useCallback, useEffect } from "react";
import moment from "moment";
import { Alert } from "react-bootstrap";
import { useSwipeable } from "react-swipeable";
import { AnimatePresence, motion } from "framer-motion";
import { useCalendar } from "../../hooks/useCalendar";
import CalendarEventDialog from "./CalendarEventDialog";
import CalendarHeader from "./CalendarHeader";
import MonthView from "./MonthView";
import WeekView from "./WeekView";
import DayView from "./DayView";
import { getRangeISO } from "./CalendarUtils";
import { VIEW_TRANSITION_MS } from "./CalendarConstants";
import "./Calendar.css";

import "bootstrap/dist/css/bootstrap.min.css";
import "moment/locale/de";

moment.locale("de");

const CalendarManager = ({ auth }) => {
  const workspaceId = localStorage.getItem("workspaceId");

  const { events, loading, error, loadEvents, addEvent, editEvent, removeEvent, modifyInstance, cancelInstance } = useCalendar({ workspaceId });

  const [viewType, setViewType] = useState("month");
  const [focusedDay, setFocusedDay] = useState(new Date());
  const [dialogState, setDialogState] = useState({ show: false, event: null, initialTimes: null });

  // Mobile default to day view
  useEffect(() => {
    if (window.innerWidth <= 768) setViewType("day");
  }, []);

  // Sync data on view/date change
  useEffect(() => {
    if (!workspaceId) return;
    const { start, end } = getRangeISO(focusedDay, viewType);
    loadEvents(start, end);
  }, [focusedDay, viewType, workspaceId, loadEvents]);

  // Normalize events
  const calendarEvents = useMemo(() => (events || []).map(e => ({
    ...e,
    start: new Date(e.start || e.startTime),
    end: new Date(e.end || e.endTime),
    isRecurring: !!e.recurrenceType || !!e.parentId,
  })), [events]);

  // Navigation
  const navigatePrevious = useCallback(() => {
    const m = moment(focusedDay);
    if (viewType === "month") m.subtract(1, "month");
    else if (viewType === "week") m.subtract(1, "week");
    else m.subtract(1, "day");
    setFocusedDay(m.toDate());
  }, [focusedDay, viewType]);

  const navigateNext = useCallback(() => {
    const m = moment(focusedDay);
    if (viewType === "month") m.add(1, "month");
    else if (viewType === "week") m.add(1, "week");
    else m.add(1, "day");
    setFocusedDay(m.toDate());
  }, [focusedDay, viewType]);

  const goToToday = useCallback(() => {
    setFocusedDay(new Date());
  }, []);

  // Dialog
  const openDialog = useCallback((event = null, slotInfo = null) => {
    setDialogState({
      show: true,
      event,
      initialTimes: slotInfo ? { start: slotInfo.start || slotInfo, end: slotInfo.end || moment(slotInfo).add(1, "hour").toDate() } : null,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setDialogState({ show: false, event: null, initialTimes: null });
  }, []);

  // Event handlers for views
  const handleEventTap = useCallback((event) => openDialog(event), [openDialog]);

  const handleEmptySlotTap = useCallback((dateTime) => {
    const end = moment(dateTime).add(1, "hour").toDate();
    openDialog(null, { start: dateTime, end });
  }, [openDialog]);

  const handleDaySelected = useCallback((day) => {
    setFocusedDay(day);
    setViewType("day");
  }, []);

  const handleDayHeaderTap = useCallback((day) => {
    setFocusedDay(day);
    setViewType("day");
  }, []);

  const handleAddEvent = useCallback(() => {
    const start = moment(focusedDay).hour(moment().hour()).startOf("hour").toDate();
    const end = moment(start).add(1, "hour").toDate();
    openDialog(null, { start, end });
  }, [focusedDay, openDialog]);

  // Swipe
  const handlers = useSwipeable({
    onSwipedLeft: navigateNext,
    onSwipedRight: navigatePrevious,
    preventDefaultTouchmoveEvent: true,
    trackMouse: false,
  });

  const transitionDuration = VIEW_TRANSITION_MS / 1000;

  return (
    <div className="cal-root" {...handlers}>
      <CalendarHeader
        focusedDay={focusedDay}
        viewType={viewType}
        onPrevious={navigatePrevious}
        onNext={navigateNext}
        onToday={goToToday}
        onAdd={handleAddEvent}
        onViewChanged={setViewType}
        loading={loading}
      />

      {error && <Alert variant="danger" className="m-2">{error}</Alert>}

      <div className="cal-view-container">
        <AnimatePresence mode="wait">
          <motion.div
            key={viewType}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: transitionDuration }}
            style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
          >
            {viewType === "month" && (
              <MonthView
                focusedDay={focusedDay}
                events={calendarEvents}
                onDaySelected={handleDaySelected}
                onEventTap={handleEventTap}
              />
            )}
            {viewType === "week" && (
              <WeekView
                focusedDay={focusedDay}
                events={calendarEvents}
                onEventTap={handleEventTap}
                onEmptySlotTap={handleEmptySlotTap}
                onDayHeaderTap={handleDayHeaderTap}
              />
            )}
            {viewType === "day" && (
              <DayView
                date={focusedDay}
                events={calendarEvents}
                onEventTap={handleEventTap}
                onEmptySlotTap={handleEmptySlotTap}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {dialogState.show && (
        <CalendarEventDialog
          show={dialogState.show}
          editingEvent={dialogState.event}
          initialTimes={dialogState.initialTimes}
          onClose={closeDialog}
          addEvent={addEvent}
          editEvent={editEvent}
          removeEvent={removeEvent}
          modifyInstance={modifyInstance}
          cancelInstance={cancelInstance}
          workspaceId={workspaceId}
          currentRange={getRangeISO(focusedDay, viewType)}
          loadEvents={loadEvents}
        />
      )}
    </div>
  );
};

export default CalendarManager;
